import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth.store'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CreditCard, AlertTriangle, ShoppingCart } from 'lucide-react'

interface SubscriptionBlockerProps {
  children: React.ReactNode
}

export const SubscriptionBlocker = ({ children }: SubscriptionBlockerProps) => {
  const navigate = useNavigate()
  const { hasActiveSubscription, user, isAdmin } = useAuthStore()
  
  if (isAdmin || hasActiveSubscription) {
    return <>{children}</>
  }
  
  const getStatusMessage = () => {
    if (!user?.subscription_status || user.subscription_status === 'inactive') {
      return {
        title: 'Abbonamento non attivo',
        description: 'Il tuo abbonamento non e\' attivo. Contatta l\'amministratore per attivare il tuo abbonamento e accedere alle funzionalita\' di trading.'
      }
    }
    if (user.subscription_status === 'expired') {
      const endDate = user.subscription_end_date 
        ? new Date(user.subscription_end_date).toLocaleDateString('it-IT')
        : '-'
      return {
        title: 'Abbonamento scaduto',
        description: `Il tuo abbonamento e\' scaduto il ${endDate}. Contatta l\'amministratore per rinnovare l\'abbonamento e continuare a utilizzare le funzionalita\' di trading.`
      }
    }
    return {
      title: 'Accesso limitato',
      description: 'Non hai accesso alle funzionalita\' di trading. Contatta l\'amministratore per maggiori informazioni.'
    }
  }
  
  const { title, description } = getStatusMessage()
  
  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Card className="border-amber-500 bg-amber-50/50 dark:bg-slate-900">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
            <CreditCard className="w-8 h-8 text-amber-500" />
          </div>
          <CardTitle className="flex items-center justify-center gap-2 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="w-5 h-5" />
            {title}
          </CardTitle>
          <CardDescription className="text-base mt-2">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg text-center">
            <p className="text-sm text-muted-foreground">
              Le funzionalita\' di trading sono disponibili solo per gli utenti con abbonamento attivo.
            </p>
          </div>
          
          <Button 
            size="lg" 
            className="w-full"
            onClick={() => navigate('/pricing')}
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            Acquista Abbonamento
          </Button>
          
          <div className="border-t pt-4 text-center">
            <p className="text-sm text-muted-foreground">
              Per assistenza, contatta il supporto o l\'amministratore della piattaforma.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
